import React, { Component, useState, useEffect, useMemo } from "react";
import { connect, useSelector } from "react-redux";
import axios from "axios";

import { Layout, Row, Col, Table, message } from "antd";
import moment from "moment-timezone";
import numberFormat from "./../../utils/numberFormat";

import { sumBy, uniq } from "lodash";

import ReportHeading from "../../utils/ReportHeading";
import { addKeysToArray } from "../../utils/utilities";
import round from "../../utils/round";
import { getDateTimeNow, edit } from "../../utils/form_utilities";
import isEmpty from "../../validation/is-empty";
import classnames from "classnames";
import { useParams } from "react-router-dom";
import {
  DELIVERY_TYPE_DELIVERED_BY_SUPPLIER,
  DELIVERY_TYPE_PICKUP_BY_CUSTOMER,
} from "../../utils/constants";
import simpleMathFunction from "../../utils/simpleMathFunction";

const { Content } = Layout;

const transaction = "customer-collections";
const url = `/api/${transaction}/`;
const title = "Customer Collection";
const date_fields = ["date", "deadline"];

const records_column = [
  {
    title: "Payment Method",
    dataIndex: ["payment_method"],
  },
  {
    title: "Bank",
    dataIndex: "bank",
  },
  {
    title: "Check No.",
    dataIndex: ["check_no"],
    align: "center",
  },
  {
    title: "Check No.",
    dataIndex: ["check_no"],
    align: "center",
  },
  {
    title: "Check Date",
    dataIndex: ["check_date"],
    align: "center",
  },
  {
    title: "Check Date",
    dataIndex: ["reference"],
  },
  {
    title: "Amount",
    dataIndex: ["amount"],
    align: "right",
    render: (value) => numberFormat(value),
  },
];

const deliveries_column = [
  {
    title: "DR#",
    dataIndex: "dr_no",
  },

  {
    title: "Date",
    dataIndex: "invoice_date",
    render: (date, record) =>
      record.footer !== 1 && moment(date).format("MM/DD/YYYY"),
  },

  {
    title: "Total Amount",
    dataIndex: "total_amount",
    width: 150,
    align: "right",
    render: (value) => numberFormat(value),
  },
  {
    title: "Remaining Balance",
    dataIndex: "balance",
    width: 150,
    align: "right",
    render: (value) => numberFormat(value),
  },
  {
    title: "Payment Amount",
    dataIndex: "payment_amount",
    align: "right",
    width: 150,
    render: (value) => numberFormat(value),
  },

  {
    title: "Balance after Payment",
    dataIndex: "payment_amount",
    width: 150,
    align: "right",
    render: (payment_amount, record) =>
      numberFormat(record.balance - (payment_amount || 0)),
  },
];

const credit_memo_column = [
  {
    title: "CM #",
    dataIndex: "cm_no",
  },
  {
    title: "Date",
    dataIndex: "date",
    render: (date, record) =>
      record.footer !== 1 && moment(date).format("MM/DD/YYYY"),
  },
  {
    title: "Reference",
    dataIndex: "reference",
  },
  {
    title: "Remarks",
    dataIndex: "remarks",
  },
  {
    title: "Total Amount",
    dataIndex: "total_amount",
    width: 150,
    align: "right",
    render: (value) => numberFormat(value),
  },
  {
    title: "Remaining CM Balance",
    dataIndex: "balance",
    width: 150,
    align: "right",
    render: (value) => <span>{value && numberFormat(value)}</span>,
  },
  {
    title: "CM Amount",
    dataIndex: "credit_amount",
    align: "right",
    width: 150,
    render: (value) => <span>{value && numberFormat(value)}</span>,
  },

  {
    title: "Unused CM after Payment",
    dataIndex: "credit_amount",
    width: 150,
    align: "right",
    render: (credit_amount, record) => (
      <span>{numberFormat(record.balance - (credit_amount || 0))}</span>
    ),
  },
];

export default function CustomerCollectionPrintout({
  match,
  reprint = false,
  duplicate = false,
}) {
  const auth = useSelector((state) => state.auth);

  const initialValues = {
    _id: null,
    po_no: null,
    date: moment(),
    supplier: null,
    remarks: "",
    items: [],

    total_amount: 0,
    total_advance_payment: 0,
    total_creditable_advance_payment: 0,
    total_credit_advance_payment: 0,
  };

  const [state, setState] = useState(initialValues);
  const [date, setDate] = useState(null);
  const [dr_numbers, setDrNumbers] = useState([]);

  const params = useParams();

  useEffect(() => {
    window.addEventListener("afterprint", (event) => {
      axios
        .post(`${url}${params.id}/print-status`, {
          user: auth.user,
        })
        .then((response) => {
          edit({
            record: { _id: response.data._id },
            setState,
            url,
            date_fields,
          });
        });
    });

    getDateTimeNow()
      .then((date) => setDate(date))
      .catch((err) =>
        message.error("There was an error getting curent date/time")
      );

    edit({
      record: { _id: params.id },
      setState,
      url,
      date_fields,
    });

    return () => {};
  }, []);

  useEffect(() => {
    let drs = [];

    if (state.sales_return?.items?.length > 0) {
      drs = state.sales_return.items.map((o) => o?.sale?.sales_no);
    }

    setDrNumbers(uniq(drs));
    return () => {};
  }, [state.sales_return, setDrNumbers]);

  return (
    <Content
      className={classnames("inventory-print print", {
        "m-t-4": duplicate,
      })}
    >
      <div>
        <Row>
          <Col span={20}>
            <ReportHeading has_logo={true} has_business_name={true} />
          </Col>
          <Col span={4}>
            {(!isEmpty(state.printed) || reprint) && (
              <div className="has-text-right has-text-weight-bold">REPRINT</div>
            )}
          </Col>
        </Row>

        <div className="has-text-centered is-size-5">{title}</div>
        <Row className="m-t-1">
          <Col span={4} className="is-flex align-items-flex-end">
            Date:
          </Col>
          <Col span={8} className="is-flex align-items-flex-end">
            {moment(state.date).format("ll")}
          </Col>
          <Col span={4} className="is-flex align-items-flex-end">
            Coll #:
          </Col>
          <Col span={8} className="is-flex align-items-flex-end">
            <span className="ref-number">{state.collection_no}</span>
          </Col>
        </Row>
        <Row>
          <Col key="label" span={4}>
            Account:
          </Col>
          <Col key="value" span={8} className="report-customer-name">
            {state.account?.name}
          </Col>
          <Col key="label" span={4}>
            References
          </Col>
          <Col key="value" span={8}>
            {state.reference}
          </Col>
        </Row>
        <Row>
          <Col span={4}>Remarks</Col>
          <Col span={8} className="pre-wrap">
            {state.remarks}
          </Col>
          <Col span={4}>Printed:</Col>
          <Col span={8}>{date && moment(date).format("lll")}</Col>
        </Row>
      </div>

      <div className="m-t-1">
        <strong>PAYMENTS</strong>
        <Table
          dataSource={addKeysToArray([
            ...(state.payments || []),
            {
              footer: 1,
              amount: sumBy(state.payments, (o) => round(o.amount)),
            },
          ])}
          columns={records_column}
          pagination={false}
          rowClassName={(record, index) => {
            if (record.footer === 1) {
              return "footer-summary has-text-weight-bold";
            }
          }}
        />
        <br />
        <strong>DELIVERIES</strong>
        <Table
          dataSource={addKeysToArray([
            ...(state.delivery_items || []),
            {
              footer: 1,
              total_amount: sumBy(state.delivery_items, (o) =>
                round(o.total_amount)
              ),
              total_returned_amount: sumBy(state.delivery_items, (o) =>
                round(o.total_returned_amount)
              ),
              net_amount: sumBy(state.delivery_items, (o) =>
                round(o.net_amount)
              ),

              balance: sumBy(state.delivery_items, (o) => round(o.balance)),

              payment_amount: sumBy(state.delivery_items, (o) =>
                round(o.payment_amount)
              ),
              adjustment_amount: sumBy(state.delivery_items, (o) =>
                round(o.adjustment_amount)
              ),
            },
          ])}
          columns={deliveries_column}
          pagination={false}
          rowClassName={(record, index) => {
            if (record.footer === 1) {
              return "footer-summary has-text-weight-bold";
            }
          }}
        />
        <br />
        <strong>CREDIT MEMO</strong>
        <Table
          dataSource={addKeysToArray([
            ...(state.credit_memo_items || []),
            {
              footer: 1,
              total_amount: sumBy(state.credit_memo_items, (o) =>
                round(o.total_amount)
              ),

              balance: sumBy(state.credit_memo_items, (o) => round(o.balance)),

              credit_amount: sumBy(state.credit_memo_items, (o) =>
                round(o.credit_amount)
              ),
            },
          ])}
          columns={credit_memo_column}
          pagination={false}
          rowClassName={(record, index) => {
            if (record.footer === 1) {
              return "footer-summary has-text-weight-bold";
            }
          }}
        />
      </div>

      <div className="signatories-container">
        <Row gutter={48}>
          <Col span={8}>Prepared by</Col>
          <Col span={8}>Checked by</Col>
          <Col span={8}>Approved by</Col>
        </Row>
        <Row gutter={48}>
          <Col span={8}>
            <div className="signatory">{state.logs?.[0]?.user?.name}</div>
          </Col>
          <Col span={8} className="has-text-centered">
            <div className="signatory">&nbsp;</div>
          </Col>
          <Col span={8}>
            <div className="signatory">&nbsp;</div>
          </Col>
        </Row>
      </div>
    </Content>
  );
}
